import { useState } from 'react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  estimateMissionRhum,
  estimateExplorationGold,
  crewWageCut,
  netAfterCrewWage,
  useLaunchExploration,
  useMyFleet,
  useMyMissions,
  useRecits,
  useLaunchExpedition,
  ongoingExplorationTo,
  type MapIslandDto,
  type StoryListItemDto,
} from '@/api/hooks'
import { useCountdown } from '@/hooks/useCountdown'
import { expeditionTierMeta } from '@/lib/expedition'
import { LAUNCHER_UNSEAWORTHY_MESSAGE } from '@/components/HullAlert'
import { GoldButton, ResourceTally } from '@/components/game/ui'
import {
  CompassIcon, RumIcon, GrimoireIcon,
} from '@/components/game/GameIcons'
import { toggleShipSelection, selectManyShips } from '@/components/game/map/fleetSelection'
import { RationsLabel, ShipPickerList } from '@/components/game/map/launcherBits'
import { combatMeta } from '@/components/game/map/shipPicker/ShipPickerRow'
import { missionFleetCap } from '@/lib/fleetGroups'
import { tripDurationSeconds, TripDuration, convoySpeedPerHour, ConvoyPace } from '@/components/game/map/tripDuration'

function ExploreLauncher({ island }: { island: MapIslandDto }) {
  const { data: fleet } = useMyFleet()
  const { data: missionsData } = useMyMissions()
  const launch = useLaunchExploration()
  const dockedShips = fleet?.ships.filter((s) => s.status === 'docked') ?? []
  const [shipId, setShipId] = useState<number | ''>('')

  // An island is explored once: if a run is already under way toward it, keep the
  // island selectable but lock the launch (the backend rejects a second one too).
  // Takes priority over the "no docked ship" notice — the lone ship may BE the
  // one out exploring this very island.
  const ongoing = ongoingExplorationTo(missionsData?.missions ?? [], island.id)
  if (ongoing) {
    return (
      <div className="bg-ocean-900/45 rounded-lg p-3 space-y-2 border border-sand-500/10">
        <p className="text-xs text-ocean-300 uppercase tracking-wider flex items-center gap-1.5">
          <CompassIcon className="w-4 h-4 text-sand-300" /> Explorer
        </p>
        <p className="text-xs text-sand-300 flex items-start gap-1.5">
          <CompassIcon className="w-4 h-4 shrink-0 mt-0.5 animate-pulse text-sand-300" />
          <span>
            Une exploration est déjà en cours vers cette île
            {ongoing.shipName ? ` (${ongoing.shipName})` : ''}.
          </span>
        </p>
        <GoldButton
          disabled
          title="Une exploration est déjà en cours vers cette île."
          className="w-full text-sm px-3 py-1.5"
        >
          Exploration en cours
        </GoldButton>
      </div>
    )
  }

  // A single-ship picker can't show a disabled row like the multi-select launchers,
  // so an unseaworthy ship is simply excluded from the options — it can't be
  // launched anyway, and the docked-ships-but-none-seaworthy case gets its own message.
  const seaworthyDocked = dockedShips.filter((s) => s.seaworthy)
  const ship = seaworthyDocked.find((s) => s.id === shipId) ?? seaworthyDocked[0] ?? null
  const origin = fleet?.island
  const distance = origin && ship ? Math.sqrt((island.x - origin.x) ** 2 + (island.y - origin.y) ** 2) : 0
  const speed = convoySpeedPerHour(ship ? [ship] : [], fleet?.travelSpeedForgeMult ?? 1)
  const roundTripHours = speed > 0 ? (distance * 2) / speed : 0
  const tripSeconds = origin && ship ? tripDurationSeconds(speed, origin, island) : 0
  const rhumEstimate = ship ? estimateMissionRhum([ship], Math.max(60, roundTripHours * 3600)) : 0
  const rhumAvailable = fleet?.resources?.rum ?? 0
  const notEnoughRhum = ship !== null && rhumEstimate > rhumAvailable
  // Distance-scaled floor — the hidden island type only pushes the real reward up.
  const goldEstimate = estimateExplorationGold(distance)
  const goldNet = netAfterCrewWage(goldEstimate)

  if (dockedShips.length === 0) {
    return (
      <div className="bg-ocean-900/45 rounded-lg p-3 space-y-2">
        <p className="text-xs text-ocean-300 italic">
          Aucun navire à quai pour explorer cette île.
        </p>
      </div>
    )
  }

  if (seaworthyDocked.length === 0) {
    return (
      <div className="bg-red-500/[0.08] rounded-lg p-3 border border-red-500/30">
        <p className="text-xs text-red-400 font-semibold">{LAUNCHER_UNSEAWORTHY_MESSAGE}</p>
      </div>
    )
  }

  return (
    <div className="bg-ocean-900/45 rounded-lg p-3 space-y-2 border border-sand-500/10">
      <p className="text-xs text-ocean-300 uppercase tracking-wider flex items-center gap-1.5"><CompassIcon className="w-4 h-4 text-sand-300" /> Explorer</p>
      <label className="block text-xs text-ocean-200">
        Navire
        <select
          value={shipId === '' ? (ship?.id ?? '') : shipId}
          onChange={(e) => setShipId(Number(e.target.value))}
          className="mt-1 w-full px-2 py-1.5 rounded bg-ocean-900 border border-ocean-500/40 focus:border-sand-300 focus:outline-none text-sm"
        >
          {seaworthyDocked.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name} ({s.shipTypeLabel}){s.hullIntegrity < 100 ? ` · coque ${s.hullIntegrity} %` : ''}
            </option>
          ))}
        </select>
      </label>
      <p className="text-xs text-ocean-300 flex items-center justify-between gap-1 flex-wrap">
        <span className="inline-flex items-center gap-1"><RationsLabel /> : <span className={`inline-flex items-center gap-1 ${notEnoughRhum ? 'text-red-400 font-semibold' : 'text-sand-300'}`}>≈ {rhumEstimate} <RumIcon className="w-3.5 h-3.5" /></span></span>
        {ship && <TripDuration seconds={tripSeconds} />}
      </p>
      <p className="text-xs text-ocean-200 inline-flex items-center gap-1 flex-wrap">
        Gain net ≈ <ResourceTally gold={goldNet} className="text-sand-300 font-semibold" />
        <span className="text-ocean-400 inline-flex items-center gap-1">· part équipage −<ResourceTally gold={crewWageCut(goldEstimate)} /></span>
      </p>
      <GoldButton
        onClick={() => {
          if (!ship) return
          launch.mutate(
            { shipId: ship.id, islandId: island.id },
            {
              onSuccess: () =>
                toast.success(`${ship.name} part en exploration vers une île inconnue.`),
              onError: (err) =>
                toast.error(err.message || 'Exploration impossible'),
            },
          )
        }}
        disabled={launch.isPending || notEnoughRhum}
        className="w-full text-sm px-3 py-1.5"
      >
        {launch.isPending ? 'Lancement…' : notEnoughRhum ? 'Rhum insuffisant' : `Envoyer en exploration (≈ ${goldNet} or net)`}
      </GoldButton>
    </div>
  )
}

/**
 * Mysterious-island CTA — a small state machine over the player's active
 * expedition on this island (read from the récits list, freshest first):
 *  - readable → "Lire le récit" (link to the Récits page)
 *  - traveling / returning → a status line + ETA countdown
 *  - none active → the expedition launcher (send a fleet)
 */
export function MysteriousExpeditionPanel({ island }: { island: MapIslandDto }) {
  const { data } = useRecits()
  const here = (data?.recits ?? []).filter((r) => r.islandName === island.name)
  const readable = here.find((r) => r.state === 'readable')
  const enRoute = here.find((r) => r.state === 'traveling' || r.state === 'returning')

  return (
    <div className="space-y-2">
      {island.expeditionTierLabel && <ExpeditionDifficulty island={island} />}
      {readable ? (
        <Link
          to={`/recits?island=${encodeURIComponent(island.name)}`}
          className="block rounded-xl p-3 text-sm transition-[filter] hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sand-300/70"
          style={{ background: '#C4B5FD1a', border: '1px solid #C4B5FD66' }}
        >
          <span className="inline-flex items-center gap-1.5" style={{ color: '#C4B5FD' }}>
            <GrimoireIcon className="w-4 h-4" />
            <strong>Lire le récit</strong>
          </span>
        </Link>
      ) : enRoute ? (
        <ExpeditionStatus recit={enRoute} />
      ) : island.hasRecit ? (
        <ExpeditionLauncher island={island} />
      ) : (
        <div className="rounded-lg p-3 text-sm" style={{ background: '#C4B5FD12', border: '1px solid #C4B5FD40' }}>
          <p className="text-xs text-ocean-300 italic">Récit à venir, aucune expédition possible ici pour l’instant.</p>
        </div>
      )}
    </div>
  )
}

/** Difficulty badge + authored fleet recommendation for a mysterious island. */
export function ExpeditionDifficulty({ island }: { island: MapIslandDto }) {
  const color = expeditionTierMeta(island.expeditionTier)?.color ?? '#C4B5FD'
  return (
    <div className="rounded-lg p-2 text-xs space-y-1" style={{ background: '#ffffff0a', border: '1px solid #ffffff14' }}>
      <p className="flex items-center justify-between gap-2">
        <span className="text-ocean-300 uppercase tracking-wider text-[10px]">Difficulté</span>
        <span
          className="inline-flex items-center px-1.5 py-0.5 rounded border text-[11px] font-semibold uppercase tracking-wider"
          style={{ color, borderColor: `${color}66`, background: `${color}1a` }}
        >
          {island.expeditionTierLabel}
        </span>
      </p>
      {island.expeditionFleetHint && (
        <p className="text-ocean-300 leading-snug">⚓ {island.expeditionFleetHint}</p>
      )}
    </div>
  )
}

/** "Flotte en route" / "Flotte en retour" status line with a live ETA. */
export function ExpeditionStatus({ recit }: { recit: StoryListItemDto }) {
  const traveling = recit.state === 'traveling'
  const eta = useCountdown(traveling ? recit.arrivesAt : recit.returnsAt)

  return (
    <div className="rounded-xl p-3 text-sm space-y-1" style={{ background: '#C4B5FD1a', border: '1px solid #C4B5FD66' }}>
      <p className="inline-flex items-center gap-1.5" style={{ color: '#C4B5FD' }}>
        <GrimoireIcon className="w-4 h-4" />
        <strong>{traveling ? 'Flotte en route' : 'Flotte en retour'}</strong>
      </p>
      <p className="text-xs text-ocean-300">
        {traveling ? 'Le récit s’ouvrira à l’accostage' : 'Tes navires regagnent le port'}
        {' · '}
        <span className="tabular-nums text-sand-300">{eta}</span>
      </p>
    </div>
  )
}

/**
 * Expedition launcher — clones the fleet-picker shape (docked ships, Chantier-naval
 * cap, rhum + duration estimate) but for a NARRATIVE run: no combat, no win-rate.
 * The rhum ration covers the full round-trip (unchanged); the OUTBOUND leg (when
 * the récit becomes readable) is half of it. Récit-purple accent (palette is
 * partial → inline styles).
 */
export function ExpeditionLauncher({ island }: { island: MapIslandDto }) {
  const { data: fleet } = useMyFleet()
  const launch = useLaunchExpedition()
  const dockedShips = fleet?.ships.filter((s) => s.status === 'docked') ?? []
  const fleetCap = missionFleetCap(fleet?.shipyard?.level)

  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const selected = selectedIds
    .map((id) => dockedShips.find((s) => s.id === id))
    .filter((s): s is NonNullable<typeof s> => Boolean(s))

  const toggleShip = (id: number) => {
    setSelectedIds((prev) =>
      toggleShipSelection(prev, id, dockedShips, fleetCap, () => toast.error(`Flotte au max (${fleetCap} navires).`)),
    )
  }

  const selectMany = (ids: number[]) => {
    setSelectedIds((prev) =>
      selectManyShips(prev, ids, dockedShips, fleetCap, () => toast.error(`Flotte au max (${fleetCap} navires).`)),
    )
  }

  if (dockedShips.length === 0) {
    return (
      <div className="rounded-lg p-3 border" style={{ background: '#C4B5FD12', borderColor: '#C4B5FD40' }}>
        <p className="text-xs text-ocean-300 italic">Aucun navire à quai pour une expédition.</p>
      </div>
    )
  }

  // Duration + rhum estimate (client-side; the server is authoritative at launch).
  const origin = fleet?.island
  const oneWayDistance = origin ? Math.sqrt((island.x - origin.x) ** 2 + (island.y - origin.y) ** 2) : 0
  const slowestSpeed = convoySpeedPerHour(selected, fleet?.travelSpeedForgeMult ?? 1)
  const roundTripHours = slowestSpeed > 0 ? (oneWayDistance * 2) / slowestSpeed : 0
  const tripSeconds = origin && selected.length > 0 ? tripDurationSeconds(slowestSpeed, origin, island) : 0
  const legSeconds = Math.round(tripSeconds / 2)
  const rhumEstimate = estimateMissionRhum(selected, Math.max(60, roundTripHours * 3600))
  const rhumAvailable = fleet?.resources?.rum ?? 0
  const notEnoughRhum = selected.length > 0 && rhumEstimate > rhumAvailable

  return (
    <div className="rounded-lg p-3 space-y-3 border" style={{ background: '#C4B5FD12', borderColor: '#C4B5FD40' }}>
      <p className="text-[11px] text-ocean-300 leading-snug">
        Envoie une flotte vers l’île mystérieuse : le récit de l’expédition s’ouvre à l’accostage.
        Tes navires restent engagés jusqu’à la fin du récit, puis rentrent, moins les pertes.
      </p>

      <ShipPickerList
        ships={dockedShips}
        selectedIds={selectedIds}
        onToggle={toggleShip}
        cap={fleetCap}
        onSelectMany={selectMany}
        onClearAll={() => setSelectedIds([])}
        meta={combatMeta}
      />

      {selected.length > 0 && (
        <div className="text-xs space-y-1 bg-ocean-900/50 rounded p-2 border border-sand-500/15">
          <div className="flex justify-between text-ocean-300">
            <RationsLabel />
            <span className={`tabular-nums inline-flex items-center gap-1 ${notEnoughRhum ? 'text-red-400 font-semibold' : 'text-sand-300'}`}>
              ≈ {rhumEstimate} <RumIcon className="w-3.5 h-3.5" />
              {notEnoughRhum && <span className="ml-1">(banque : {rhumAvailable})</span>}
            </span>
          </div>
          <div className="flex justify-between text-ocean-300">
            <span>Aller (récit lisible dans)</span>
            <TripDuration seconds={legSeconds} />
          </div>
          <ConvoyPace ships={selected} tripSeconds={legSeconds} />
        </div>
      )}

      <p className="text-ocean-300 text-[11px]">
        Seuls les navires envoyés risquent d’être perdus ; la flotte laissée au port ne risque rien.
      </p>

      <button
        onClick={() => {
          if (selected.length === 0) return
          const shipIds = selected.map((s) => s.id)
          launch.mutate(
            { islandId: island.id, shipIds },
            {
              onSuccess: () => toast.success(`Expédition en route vers ${island.name}.`),
              onError: (err) => toast.error(err.message || 'Expédition impossible'),
            },
          )
          setSelectedIds([])
        }}
        disabled={launch.isPending || selected.length === 0 || notEnoughRhum}
        className="w-full text-sm px-3 py-1.5 rounded-md font-semibold transition-[filter] hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1.5"
        style={{ background: '#C4B5FD', color: '#1b1033' }}
      >
        {launch.isPending ? (
          'Lancement…'
        ) : notEnoughRhum ? (
          'Rhum insuffisant'
        ) : (
          <><GrimoireIcon className="w-4 h-4" /> Lancer l’expédition ({selected.length})</>
        )}
      </button>
    </div>
  )
}

export { ExploreLauncher }
